import * as THREE from 'three';

/**
 * Creates an embedded image object for Three.js scene
 * This image will be exported as OLE2FRAME in DXF files
 *
 * @param {Object} options - Image options
 * @param {string} options.src - Image source URL or data URL
 * @param {number} options.x - X position in scene
 * @param {number} options.y - Y position in scene
 * @param {number} [options.z=0] - Z position in scene
 * @param {number} [options.width] - Display width (auto-calculated if not provided)
 * @param {number} [options.height] - Display height (auto-calculated if not provided)
 * @param {number} [options.scale=1] - Scale factor
 * @param {string} [options.layerName='0'] - Layer name for DXF export
 * @param {string} [options.name='embeddedImage'] - Object name
 * @returns {Promise<THREE.Mesh>} Three.js mesh with image texture
 */
export async function createEmbeddedImage(options) {
  const {
    src,
    x = 0,
    y = 0,
    z = 0,
    width: requestedWidth,
    height: requestedHeight,
    scale = 1,
    layerName = '0',
    name = 'embeddedImage'
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      // Get image dimensions
      const imgWidth = img.naturalWidth || img.width;
      const imgHeight = img.naturalHeight || img.height;

      // Calculate display dimensions (maintain aspect ratio if only one dimension given)
      let displayWidth = requestedWidth;
      let displayHeight = requestedHeight;

      if (!displayWidth && !displayHeight) {
        // Use natural dimensions scaled to reasonable size
        const maxDimension = 100; // Default max dimension
        const aspectRatio = imgWidth / imgHeight;
        if (imgWidth > imgHeight) {
          displayWidth = maxDimension;
          displayHeight = maxDimension / aspectRatio;
        } else {
          displayHeight = maxDimension;
          displayWidth = maxDimension * aspectRatio;
        }
      } else if (!displayWidth) {
        displayWidth = displayHeight * (imgWidth / imgHeight);
      } else if (!displayHeight) {
        displayHeight = displayWidth * (imgHeight / imgWidth);
      }

      // Apply scale
      displayWidth *= scale;
      displayHeight *= scale;

      // Create canvas and get image data for OLE2FRAME export
      const canvas = document.createElement('canvas');
      canvas.width = imgWidth;
      canvas.height = imgHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, imgWidth, imgHeight);

      // Create Three.js texture
      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;

      // Create plane geometry and mesh
      const geometry = new THREE.PlaneGeometry(displayWidth, displayHeight);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(x + displayWidth / 2, y + displayHeight / 2, z);
      mesh.name = name;

      // Store data for DXF export
      mesh.userData = {
        type: 'embeddedImage',
        imageData: {
          data: imageData.data,
          width: imgWidth,
          height: imgHeight
        },
        width: displayWidth,
        height: displayHeight,
        scale: 1, // Scale already applied
        layerName,
        originalWidth: imgWidth,
        originalHeight: imgHeight,
        src
      };

      resolve(mesh);
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${src}`));
    };

    img.src = src;
  });
}

/**
 * Creates an embedded image from a File object
 * @param {File} file - Image file
 * @param {Object} options - Position and size options (same as createEmbeddedImage minus src)
 * @returns {Promise<THREE.Mesh>} Three.js mesh with image texture
 */
export async function createEmbeddedImageFromFile(file, options = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const mesh = await createEmbeddedImage({
          ...options,
          src: e.target.result,
          name: options.name || file.name
        });
        resolve(mesh);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Creates an embedded image from canvas element
 * @param {HTMLCanvasElement} canvas - Source canvas
 * @param {Object} options - Position and size options
 * @returns {THREE.Mesh} Three.js mesh with image texture
 */
export function createEmbeddedImageFromCanvas(canvas, options = {}) {
  const {
    x = 0,
    y = 0,
    z = 0,
    width: requestedWidth,
    height: requestedHeight,
    scale = 1,
    layerName = '0',
    name = 'embeddedImage'
  } = options;

  const imgWidth = canvas.width;
  const imgHeight = canvas.height;

  // Calculate display dimensions
  let displayWidth = requestedWidth || imgWidth / 10;
  let displayHeight = requestedHeight || imgHeight / 10;
  displayWidth *= scale;
  displayHeight *= scale;

  // Get image data
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, imgWidth, imgHeight);

  // Create Three.js texture
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;

  // Create plane geometry and mesh
  const geometry = new THREE.PlaneGeometry(displayWidth, displayHeight);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    side: THREE.DoubleSide,
    transparent: true
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(x + displayWidth / 2, y + displayHeight / 2, z);
  mesh.name = name;

  // Store data for DXF export
  mesh.userData = {
    type: 'embeddedImage',
    imageData: {
      data: imageData.data,
      width: imgWidth,
      height: imgHeight
    },
    width: displayWidth,
    height: displayHeight,
    scale: 1,
    layerName,
    originalWidth: imgWidth,
    originalHeight: imgHeight
  };

  return mesh;
}

/**
 * Updates an embedded image's position
 * @param {THREE.Mesh} mesh - The embedded image mesh
 * @param {number} x - New X position
 * @param {number} y - New Y position
 * @param {number} [z] - New Z position (optional)
 */
export function updateEmbeddedImagePosition(mesh, x, y, z) {
  const width = mesh.userData.width || 100;
  const height = mesh.userData.height || 100;
  mesh.position.set(x + width / 2, y + height / 2, z ?? mesh.position.z);
}

/**
 * Updates an embedded image's scale
 * @param {THREE.Mesh} mesh - The embedded image mesh
 * @param {number} scale - New scale factor
 */
export function updateEmbeddedImageScale(mesh, scale) {
  const originalWidth = mesh.userData.originalWidth || 100;
  const originalHeight = mesh.userData.originalHeight || 100;

  const newWidth = (originalWidth / 10) * scale;
  const newHeight = (originalHeight / 10) * scale;

  mesh.geometry.dispose();
  mesh.geometry = new THREE.PlaneGeometry(newWidth, newHeight);

  mesh.userData.width = newWidth;
  mesh.userData.height = newHeight;
  mesh.userData.scale = scale;
}

export default {
  createEmbeddedImage,
  createEmbeddedImageFromFile,
  createEmbeddedImageFromCanvas,
  updateEmbeddedImagePosition,
  updateEmbeddedImageScale
};
